import { useMemo, useState } from 'react';
import { hotRanking, type RankTab } from '@/mocks/homeContent';
import SectionHead from './SectionHead';

const avatarColors = ['bg-primary-500', 'bg-accent-600', 'bg-secondary-600', 'bg-foreground-800'];

const tabs: { key: RankTab; label: string }[] = [
  { key: 'all', label: '综合热度榜' },
  { key: 'rising', label: '上升新势力' },
  { key: 'stable', label: '口碑稳定盘' },
];

function TrendBadge({ trend }: { trend: 'up' | 'down' | 'flat' }) {
  if (trend === 'up') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-accent-600">
        <i className="ri-arrow-up-line" aria-hidden="true"></i> 上升
      </span>
    );
  }
  if (trend === 'down') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600">
        <i className="ri-arrow-down-line" aria-hidden="true"></i> 回落
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-secondary-700">
      <i className="ri-subtract-line" aria-hidden="true"></i> 稳定
    </span>
  );
}

export default function Rankings() {
  const [tab, setTab] = useState<RankTab>('all');
  const list = useMemo(() => {
    if (tab === 'rising') return hotRanking.filter((r) => r.trend === 'up');
    if (tab === 'stable') return hotRanking.filter((r) => r.trend === 'flat');
    return hotRanking;
  }, [tab]);

  return (
    <section id="rankings" className="scroll-mt-24 bg-background-100 py-12 md:py-16">
      <div className="container-x">
        <SectionHead
          eyebrow="每月滚动更新"
          title="搭讪博主热榜排行"
          sub="口径：内容真实度 30% · 可复制性 20% · 更新稳定 20% · 互动口碑 20% · 翻车率 10%。本期基于近 30 天公开内容独立观测。"
        />

        <div className="mb-6 flex justify-center">
          <div className="inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition ${
                  tab === t.key
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3">
          {list.map((item, idx) => (
            <article
              key={item.name}
              className="group grid grid-cols-1 items-center gap-4 rounded-2xl border border-background-200 bg-background-50 p-4 transition hover:border-primary-200 hover:shadow-soft md:grid-cols-[auto_1fr_auto] md:gap-6 md:px-5"
            >
              <div className="flex items-center gap-4">
                <span
                  className={`flex h-11 w-9 flex-shrink-0 items-center justify-center rounded-md font-heading text-lg font-black ${
                    item.rank === 1
                      ? 'bg-primary-500 text-background-50'
                      : item.rank === 2
                        ? 'bg-primary-300 text-background-50'
                        : item.rank === 3
                          ? 'bg-secondary-600 text-background-50'
                          : 'bg-background-200 text-foreground-700'
                  }`}
                >
                  {item.rank}
                </span>
                <span
                  className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full ${avatarColors[idx % avatarColors.length]} text-base font-bold text-background-50`}
                >
                  {item.name.charAt(0)}
                </span>
                <div className="flex flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-heading text-base font-bold text-foreground-950">{item.name}</h3>
                    <TrendBadge trend={item.trend} />
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="chip bg-background-100 px-2 py-0.5 text-xs text-foreground-600">{item.platform}</span>
                    <span className="chip bg-accent-50 px-2 py-0.5 text-xs text-accent-700">{item.tag}</span>
                    <span className="text-xs text-foreground-500">{item.fans} 粉丝</span>
                  </div>
                </div>
              </div>

              <p className="hidden text-sm leading-6 text-foreground-600 sm:block md:pl-2">{item.summary}</p>

              <div className="flex items-center justify-between gap-4 md:justify-end">
                <div className="flex flex-col items-end gap-1">
                  <span className="text-xs text-foreground-500">综合分</span>
                  <span className="flex items-center gap-1 font-heading text-2xl font-black text-primary-600">{item.score}</span>
                </div>
                <a
                  href="#reviews"
                  className="hidden items-center gap-1 whitespace-nowrap rounded-full border border-background-200 px-3 py-1.5 text-xs font-semibold text-foreground-700 transition hover:border-primary-300 hover:text-primary-600 md:inline-flex"
                >
                  看测评
                  <i className="ri-arrow-right-s-line text-sm" aria-hidden="true"></i>
                </a>
              </div>
            </article>
          ))}
        </div>

        <p className="mt-4 text-center text-xs text-foreground-500">
          榜单为编辑部独立观点，不构成任何好恶评价；评分随内容更新每月滚动调整。
        </p>
      </div>
    </section>
  );
}